import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import {
	ContactType,
	useAccessibleUserGroupsLazyQuery,
	useGetContactLazyQuery,
	type UserGroup,
} from "@repo/server-api";
import { useBoundDeep, type ErrorMap } from "@repo/shared/common";
import { BoundAsyncSelect, useAsyncSelectContactQuery } from "@repo/shared/components";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUserMutations } from "../hooks/useUserMutations";
import { emptyCreateLoginModel, type CreateLoginModel } from "../types/UserTypes";

export type CreateLoginDialogHandle = {
	show: () => void;
};

export type CreateLoginDialogProps = {
	onSuccess?: () => void;
};

/**
 * Normalizes a string for use as a username:
 * - Converts to lowercase
 * - Replaces German umlauts (ä→ae, ö→oe, ü→ue, ß→ss)
 * - Removes special characters and spaces
 */
function normalizeUsername(input: string): string {
	return input
		.toLowerCase()
		.replace(/ä/g, "ae")
		.replace(/ö/g, "oe")
		.replace(/ü/g, "ue")
		.replace(/ß/g, "ss")
		.replace(/[^a-z0-9.]/g, "");
}

function generateUsername(firstName: string, lastName: string): string {
	const normalizedFirst = normalizeUsername(firstName);
	const normalizedLast = normalizeUsername(lastName);
	return `${normalizedFirst}.${normalizedLast}`;
}

export const CreateLoginDialog = forwardRef<CreateLoginDialogHandle, CreateLoginDialogProps>(
	({ onSuccess }, ref) => {
		const { t } = useTranslation();

		const [open, setOpen] = useState(false);
		const [model, setModel] = useState<CreateLoginModel>(emptyCreateLoginModel);
		const [errors, setErrors] = useState<ErrorMap>({});
		const [availableGroups, setAvailableGroups] = useState<UserGroup[]>([]);

		// Contact details fetched after selection
		const [contactDetails, setContactDetails] = useState<{
			firstName: string;
			lastName: string;
			email: string | null;
		} | null>(null);

		const { createUser, isLoading } = useUserMutations();

		// Contact search hook
		const contactSearchHook = useAsyncSelectContactQuery(
			[ContactType.Employee, ContactType.Freelancer],
			false,
			false,
		);

		// Fetch contact details
		const [getContact] = useGetContactLazyQuery();

		const [getAccessibleGroups] = useAccessibleUserGroupsLazyQuery({
			onCompleted: (data) => {
				if (data?.accessibleUserGroups) {
					const filteredGroups = data.accessibleUserGroups.filter((group) =>
						[
							"employee",
							"freelancer",
							"freelancer_of_supplier",
							"project_manager",
							"tenant_administrator",
							"tenant_hr",
							"contractor_administrator",
							"contractor_hr",
							"accountant",
						].includes(group.name),
					);
					setAvailableGroups(filteredGroups);
				}
			},
		});

		// Bindings
		const contactField = useBoundDeep(model, setModel, (m) => m.contact, errors, setErrors);
		const loginField = useBoundDeep(model, setModel, (m) => m.login, errors, setErrors);
		const sendMailField = useBoundDeep(model, setModel, (m) => m.sendMail, errors, setErrors);

		useImperativeHandle(ref, () => ({
			show: () => {
				setModel(emptyCreateLoginModel);
				setErrors({});
				setContactDetails(null);
				contactSearchHook.resetSearch();
				getAccessibleGroups();
				setOpen(true);
			},
		}));

		// Fetch contact details when contact is selected
		useEffect(() => {
			if (model.contact) {
				getContact({ variables: { id: model.contact.id } }).then((result) => {
					const contact = result.data?.node;
					if (contact && contact.__typename === "Contact") {
						// Find email from additionalInformation
						const emailInfo = contact.additionalInformation?.find(
							(info) => info.__typename === "EmailInformation",
						);
						const email = emailInfo && "emailAddress" in emailInfo ? emailInfo.emailAddress : null;

						setContactDetails({
							firstName: contact.displayName.split(" ")[0] || "",
							lastName: contact.displayName.split(" ").slice(1).join(" ") || "",
							email,
						});

						// Auto-generate username
						const firstName = contact.displayName.split(" ")[0] || "";
						const lastName = contact.displayName.split(" ").slice(1).join(" ") || "";
						const generatedLogin = generateUsername(firstName, lastName);
						setModel((prev) => ({ ...prev, login: generatedLogin }));
					}
				});
			} else {
				setContactDetails(null);
			}
		}, [model.contact, getContact]);

		const handleClose = useCallback(() => {
			setOpen(false);
		}, []);

		const handleGroupToggle = useCallback((groupName: string) => {
			setModel((prev) => {
				const isSelected = prev.groups.includes(groupName);
				const newGroups = isSelected
					? prev.groups.filter((g) => g !== groupName)
					: [...prev.groups, groupName];
				return { ...prev, groups: newGroups };
			});
			setErrors((prev) => ({ ...prev, groups: "" }));
		}, []);

		const getGroupIds = useCallback(
			(groupNames: string[]): string[] => {
				return groupNames
					.map((name) => availableGroups.find((g) => g.name === name)?.id)
					.filter((id): id is string => id !== undefined);
			},
			[availableGroups],
		);

		const validate = useCallback((): boolean => {
			const newErrors: ErrorMap = {};

			if (!model.contact) {
				newErrors.contact = t("validation.contact-required");
			}

			if (!contactDetails?.email) {
				newErrors.email = t("no-email-warning");
			}

			if (!model.login.trim()) {
				newErrors.login = t("validation.username-required");
			}

			if (model.groups.length === 0) {
				newErrors.groups = t("validation.groups-required");
			}

			setErrors(newErrors);
			return Object.keys(newErrors).length === 0;
		}, [model, contactDetails, t]);

		const handleSubmit = useCallback(async () => {
			if (!validate() || !model.contact || !contactDetails) return;

			const groupIds = getGroupIds(model.groups);

			const success = await createUser({
				contactId: model.contact.id,
				email: contactDetails.email ?? "",
				firstName: contactDetails.firstName,
				lastName: contactDetails.lastName,
				username: model.login.trim(),
				groupIds,
				sendVerificationEmail: model.sendMail,
			});

			if (success) {
				handleClose();
				onSuccess?.();
			}
		}, [validate, model, contactDetails, getGroupIds, createUser, handleClose, onSuccess]);

		return (
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>{t("create-login")}</DialogTitle>
				<DialogContent>
					<Stack spacing={3} sx={{ mt: 1 }}>
						{/* Contact Search */}
						<BoundAsyncSelect
							label={t("contact")}
							boundedValue={contactField}
							searchHook={contactSearchHook}
						/>

						{/* Email warning */}
						{model.contact && !contactDetails?.email && (
							<Typography variant="body2" color="error">
								{t("no-email-warning")}
							</Typography>
						)}

						{/* Username field */}
						<TextField
							label={t("username")}
							value={loginField.value}
							onChange={(e) => {
								loginField.onChangeValue(normalizeUsername(e.target.value));
							}}
							error={!!errors.login}
							helperText={errors.login}
							fullWidth
							disabled={!model.contact}
						/>

						{/* Groups list */}
						<Stack spacing={0.5}>
							<Typography variant="body2" color={errors.groups ? "error" : "text.secondary"}>
								{t("groups")}
							</Typography>
							<Box
								sx={{
									maxHeight: 500,
									overflow: "auto",
									border: "1px solid",
									borderColor: errors.groups ? "error.main" : "divider",
									borderRadius: 1,
									p: 1,
								}}
							>
								<FormGroup>
									{availableGroups.map((group) => (
										<FormControlLabel
											key={group.id}
											control={
												<Checkbox
													checked={model.groups.includes(group.name)}
													onChange={() => handleGroupToggle(group.name)}
													size="small"
												/>
											}
											label={t(`groups.${group.name}`)}
										/>
									))}
								</FormGroup>
							</Box>
							{errors.groups && <FormHelperText error>{errors.groups}</FormHelperText>}
						</Stack>

						{/* Send verification email toggle */}
						<FormControlLabel
							control={
								<Switch
									checked={sendMailField.value}
									onChange={(e) => sendMailField.onChangeValue(e.target.checked)}
								/>
							}
							label={t("send-verification-email")}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={handleClose}>
						{t("cancel")}
					</Button>
					<LoadingButton
						loading={isLoading}
						variant="contained"
						onClick={handleSubmit}
						disabled={!model.contact || !contactDetails?.email}
					>
						{t("save")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		);
	},
);

CreateLoginDialog.displayName = "CreateLoginDialog";
